// Automations → « Processus de l'agent » (owner, 2026-10-15 — approved sketch
// https://claude.ai/artifact/JjRwPRYxftXA5dj9QEnqfe). Two paths, ONE step library;
// every save is a new version, one click goes back. ⚠️ The agent does not read this
// yet: worker routes/process.ts, Plan 2 wires the engine behind a switch (OFF).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { processApi, type OrderError, type PathKey, type ProcessDoc, type StepSettings } from '@/lib/processApi';
import { moveStep, toggleStep } from '@/lib/processOrder';
import { StepList } from './StepList';
import { StepPanel } from './StepPanel';
import { SiteFieldsCard } from './SiteFieldsCard';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function ProcessTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['process'], queryFn: () => processApi.get() });
  // The draft: null = no edit yet, the active version is shown as it is.
  const [draft, setDoc] = useState<ProcessDoc | null>(null);
  const doc = draft ?? data?.active.doc ?? null;
  const [path, setPath] = useState<PathKey>('whatsapp');
  const [selected, setSelected] = useState<string | null>(null);
  const [errors, setErrors] = useState<OrderError[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [mobileStep, setMobileStep] = useState(false);
  const seq = useRef(0);

  // The order check runs on the worker (one rulebook) — 300 ms after the last change.
  useEffect(() => {
    if (!doc) return;
    const mine = ++seq.current;
    const h = setTimeout(() => {
      processApi.validate(doc).then((r) => { if (mine === seq.current) setErrors(r.errors); }).catch(() => {});
    }, 300);
    return () => clearTimeout(h);
  }, [doc]);

  const catalog = useMemo(() => new Map((data?.catalog ?? []).map((s) => [s.key, s])), [data]);
  const locked = useMemo(() => new Set((data?.catalog ?? []).filter((s) => s.locked).map((s) => s.key)), [data]);
  const nextVersion = Math.max(0, ...(data?.versions ?? []).map((v) => v.version)) + 1;
  const changed = Boolean(data && draft && !same(draft, data.active.doc));

  // Saved = a new version, NOT yet active: the owner activates it on purpose.
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (note: string) => processApi.save(doc!, note),
    onSuccess: (r) => { setSavedVersion(r.version); setDoc(null); qc.invalidateQueries({ queryKey: ['process'] }); },
  });
  const activate = useMutation({
    mutationFn: (v: number) => processApi.activate(v),
    onSuccess: () => { setDoc(null); setSavedVersion(null); qc.invalidateQueries({ queryKey: ['process'] }); },
  });

  if (isLoading) return <div className="text-[13px] text-gray-500 py-6">{t('Chargement…')}</div>;
  if (isError || !data || !doc) return <div className="text-[13px] text-red-700 py-6">{t('Lecture impossible :')} {(error as Error)?.message ?? ''}</div>;

  const list = doc.paths[path];
  const sel = selected ?? list[1]?.key ?? list[0]?.key;
  const selDef = sel ? catalog.get(sel) : undefined;
  const selIndex = list.findIndex((s) => s.key === sel);
  const pathErrors = errors.filter((e) => e.path === path);
  const otherErrors = errors.length - pathErrors.length;
  const countChanges = (['whatsapp', 'site'] as PathKey[]).reduce((n, p) => n + doc.paths[p].filter((s, i) => !same(s, data.active.doc.paths[p][i])).length, 0)
    + Object.keys(doc.steps).filter((k) => !same(doc.steps[k], data.active.doc.steps[k])).length;

  const setList = (next: typeof list) => setDoc({ ...doc, paths: { ...doc.paths, [path]: next } });
  const setSettings = (key: string, s: StepSettings) => setDoc({ ...doc, steps: { ...doc.steps, [key]: s } });
  const onMove = (key: string, to: number) => { if (!locked.has(key)) setList(moveStep(list, key, to)); };
  const onToggle = (key: string) => setList(toggleStep(list, key, locked));

  const tabBtn = (p: PathKey, label: string) => {
    const off = doc.paths[p].filter((s) => !s.on).length;
    return (
      <button type="button" role="tab" aria-selected={path === p} onClick={() => { setPath(p); setMobileStep(false); }}
        className={`h-12 px-4 border-b-[3px] text-[15px] cursor-pointer flex items-center gap-2 whitespace-nowrap ${path === p ? 'border-adm-gold font-bold text-gray-900' : 'border-transparent font-semibold text-gray-500'}`}>
        {t(label)} <span className="hidden md:inline text-[12px] font-medium text-gray-500">{t('{n} étapes · {o} OFF', { n: doc.paths[p].length, o: off })}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-24">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[28px] font-semibold text-gray-900 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>{t('Processus de l’agent')}</h1>
          <p className="text-[13px] text-gray-600 mt-1">
            {t('Version {v} active.', { v: data.active.version })} {t('Une nouvelle version ne touche que les nouvelles conversations.')}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowVersions((v) => !v)} aria-expanded={showVersions}
            className="h-11 px-4 rounded-lg border border-gray-300 bg-white text-[14px] font-semibold text-gray-900 cursor-pointer">{t('Historique des versions')}</button>
        </div>
      </div>

      <p role="status" className="text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        {data.engineEnabled ? t('Le moteur de l’agent suit ce processus.') : t('Le moteur de l’agent n’utilise pas encore ce processus (interrupteur OFF) — rien ne change pour les clientes.')}
        {data.tableMissing && <> {t('Lecture seule : la migration 251 n’est pas encore appliquée.')}</>}
      </p>

      {savedVersion && (
        <div role="status" className="flex items-center gap-3 flex-wrap text-[13px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <span className="flex-1">{t('Version {v} enregistrée — pas encore active.', { v: savedVersion })}</span>
          <button type="button" disabled={activate.isPending}
            onClick={() => { if (window.confirm(t('Activer la version {v} ? Elle s’appliquera aux nouvelles conversations.', { v: savedVersion }))) activate.mutate(savedVersion); }}
            className="h-10 px-4 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[13px] font-bold cursor-pointer">{t('Activer la version {v}', { v: savedVersion })}</button>
        </div>
      )}

      {showVersions && (
        <section aria-label={t('Versions')} className="bg-white border border-gray-200 rounded-2xl p-4">
          <ol className="flex flex-col gap-2">
            {[...data.versions].reverse().map((v) => (
              <li key={v.version} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${v.is_active ? 'border-2 border-adm-gold' : 'border border-gray-200'}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold text-gray-900">{t('Version {v}', { v: v.version })}{v.is_active ? ` · ${t('en service')}` : ''}</div>
                  <div className="text-[12px] text-gray-500 truncate">{v.note ?? '—'}{v.created_by ? ` · ${v.created_by}` : ''}{v.created_at ? ` · ${new Date(v.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}</div>
                </div>
                {!v.is_active && !data.tableMissing && (
                  <button type="button" disabled={activate.isPending}
                    onClick={() => { if (window.confirm(t('Activer la version {v} ? Elle s’appliquera aux nouvelles conversations.', { v: v.version }))) activate.mutate(v.version); }}
                    className="h-10 px-3 rounded-lg border border-gray-300 text-[13px] font-semibold text-gray-900 cursor-pointer">{t('Revenir à cette version')}</button>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      <div role="tablist" aria-label={t('Parcours')} className="flex border-b border-gray-200 overflow-x-auto">
        {tabBtn('whatsapp', 'WhatsApp')}
        {tabBtn('site', 'Shopify / site')}
        <span className="hidden md:inline ml-auto self-center text-[12.5px] text-gray-500 pl-3 whitespace-nowrap">{t('Test A/B : désactivé')}</span>
      </div>

      {pathErrors.length > 0 && (
        <div role="alert" className="text-[13px] text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <strong>{t('Ordre impossible — enregistrement bloqué.')}</strong> {pathErrors[0].message}
        </div>
      )}
      {otherErrors > 0 && (
        <div role="alert" className="text-[12.5px] text-red-800">{t('L’autre onglet a aussi {n} problème(s).', { n: otherErrors })}</div>
      )}

      <div className="grid gap-5 md:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
        <div className={mobileStep ? 'hidden md:block' : ''}>
          <StepList path={path} list={list} catalog={catalog} settings={doc.steps} errors={errors} selected={sel ?? null}
            onSelect={(k) => { setSelected(k); setMobileStep(true); }} onMove={onMove} onToggle={onToggle} />
        </div>
        <div className={`flex flex-col gap-4 ${mobileStep ? '' : 'hidden md:flex'}`}>
          {selDef && sel && (
            <StepPanel def={selDef} index={selIndex} on={list[selIndex]?.on ?? false} settings={doc.steps[sel]} catalog={data.catalog}
              knownFields={data.knownFields} onChange={(s) => setSettings(sel, s)} onToggle={() => onToggle(sel)}
              onBack={mobileStep ? () => setMobileStep(false) : undefined} />
          )}
          {path === 'site' && <SiteFieldsCard />}
        </div>
      </div>

      <footer className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 px-4 md:px-8 py-3 flex items-center gap-2 md:gap-3">
        <span className="text-[12.5px] md:text-[13px] text-gray-600 flex-1 min-w-0">
          <span className="hidden sm:inline">{changed ? t('{n} modification(s) non enregistrée(s)', { n: countChanges }) : t('Aucune modification')}</span>
          <span className="sm:hidden">{changed ? t('{n} modif.', { n: countChanges }) : t('—')}</span>
          {save.isError && <span className="text-red-700"> · {(save.error as Error)?.message}</span>}
        </span>
        <button type="button" disabled={!changed} onClick={() => setDoc(null)}
          className="shrink-0 h-11 px-3 md:px-4 rounded-lg border border-gray-300 bg-white text-[14px] font-semibold text-gray-900 disabled:opacity-40 cursor-pointer">{t('Annuler')}</button>
        <button type="button" disabled title={t('Arrive avec le plan 3')}
          className="hidden md:inline-flex items-center h-11 px-4 rounded-lg border border-gray-300 bg-white text-[14px] font-semibold text-gray-400">{t('Tester dans le simulateur')}</button>
        <button type="button" disabled={!changed || errors.length > 0 || data.tableMissing || save.isPending}
          onClick={() => { const note = window.prompt(t('Qu’est-ce qui change dans cette version ?')) ?? ''; save.mutate(note); }}
          className="shrink-0 h-11 px-4 md:px-5 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[14px] font-bold disabled:bg-gray-200 disabled:text-gray-500 cursor-pointer whitespace-nowrap">
          {t('Enregistrer la version {v}', { v: nextVersion })}
        </button>
      </footer>
    </div>
  );
}
